// 🆕 2026-09-01 · Konsens-Review (Prozess 4a) — das Abhak-Panel.
//
// Der Kamera-Konsens misst nur Geometrie: "steht diese Kamera weit weg
// von den übrigen?" Das kann eine degenerierte Pose sein (Shot 8:
// 310 m) ODER ein legitimer zweiter Kamerastandort (Shot 38: 0,4 m
// Bewegung, einfach eine andere Kamera). Nur ein Mensch kann das
// unterscheiden — und er soll es EINMAL tun, statt dieselbe Warnung
// bei jedem Blick zu sehen. Muster: Auto-NP-Review.
//
// Zwei Antworten je Shot:
//   ✓ Eigener Standort  — Warnung verschwindet dauerhaft, der
//                         Label-Export erntet die Shots wieder.
//   ✕ Pose verwerfen    — Selfcal-Eintrag weg (Anker bleiben), der
//                         Provider fällt auf Anker-Slerp zurück.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  fehlerText, getShotSelfcal, konsensOkSetzen, selfcalPoseVerwerfen,
} from "../../lib/api";

export default function KonsensPanel({ seqId, refreshKey = 0, onChanged }) {
  const [offene, setOffene] = useState([]);
  const [laufend, setLaufend] = useState(null);

  const laden = useCallback(() => {
    if (!seqId) return;
    getShotSelfcal(seqId)
      .then((d) => {
        const je = d?.kamera_konsens?.je_shot || {};
        setOffene(
          Object.entries(je)
            .filter(([, v]) => v.auffaellig && !v.bestaetigt)
            .map(([sid, v]) => ({ sid, ...v }))
            .sort((a, b) => Number(a.sid) - Number(b.sid))
        );
      })
      .catch(() => setOffene([]));
  }, [seqId]);

  useEffect(() => { laden(); }, [laden, refreshKey]);

  const antworten = async (sid, aktion) => {
    setLaufend(sid);
    try {
      if (aktion === "ok") {
        await konsensOkSetzen(seqId, sid);
        toast.success(`Shot ${sid}: als eigener Kamerastandort bestätigt — Labels wieder erntbar`);
      } else {
        await selfcalPoseVerwerfen(seqId, sid);
        toast.success(`Shot ${sid}: Pose verworfen — Provider nutzt wieder Anker-Interpolation`);
      }
      laden();
      onChanged?.();
    } catch (e) {
      toast.error(fehlerText(e, "Aktion fehlgeschlagen"));
    } finally {
      setLaufend(null);
    }
  };

  if (!offene.length) return null;

  return (
    <div
      data-testid="konsens-panel"
      className="rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 space-y-1"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-amber-300">
        ⚠ Kamera-Konsens · {offene.length} Shot(s) zu prüfen
      </div>
      {offene.map((s) => (
        <div
          key={s.sid}
          data-testid={`konsens-card-${s.sid}`}
          className="flex flex-wrap items-center gap-2 text-[11px] text-amber-100/90"
        >
          <span className="font-semibold">Shot {s.sid}</span>
          <span className="font-mono text-[10px] text-amber-200/70">
            {Math.round(s.abstand_m ?? 0)} m neben den übrigen Kameras
            {s.position_m ? ` · Position [${s.position_m.map((v) => Math.round(v)).join(", ")}]` : ""}
          </span>
          <button
            data-testid={`konsens-ok-${s.sid}`}
            disabled={laufend === s.sid}
            onClick={() => antworten(s.sid, "ok")}
            className="rounded border border-emerald-500/50 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
            title="Diese Kamera steht wirklich woanders (z. B. Endzonen-Kamera). Die Warnung verschwindet dauerhaft, und der Label-Export erntet die Shots dieses Standorts wieder."
          >
            ✓ Eigener Standort
          </button>
          <button
            data-testid={`konsens-verwerfen-${s.sid}`}
            disabled={laufend === s.sid}
            onClick={() => antworten(s.sid, "verwerfen")}
            className="rounded border border-red-500/50 bg-red-500/10 px-1.5 py-0.5 text-[10px] text-red-300 hover:bg-red-500/20 disabled:opacity-50"
            title="Die Pose ist degeneriert (Kamera weit außerhalb des Plausiblen). Nur der Selfcal-Eintrag wird gelöscht — Anker und Klicks bleiben; der nächste Anker rechnet automatisch neu."
          >
            ✕ Pose verwerfen
          </button>
        </div>
      ))}
    </div>
  );
}
